import React, { useEffect, useMemo, useState } from 'react'
import { Button, Paper, Chip, IconButton, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import metaWhite from '../../assets/meta-white-color.png'
import metaPrimary from '../../assets/meta-primary-color.png'
import Text from '../Text'

const DISMISS_TTL_MS = 24 * 60 * 60 * 1000
const COUNTDOWN_TICK_MS = 1000

const dismissKey = (apiId, version) => `meta-upgrade-promo-dismissed:${apiId || 'global'}:${version || ''}`

const isWithinWindow = (startDate, endDate) => {
  const now = Date.now()
  if (startDate) {
    const start = new Date(startDate).getTime()
    if (Number.isFinite(start) && now < start) return false
  }
  if (endDate) {
    const end = new Date(endDate).getTime()
    if (Number.isFinite(end) && now > end) return false
  }
  return true
}

const formatCountdown = (ms) => {
  if (ms <= 0) return null
  const totalSeconds = Math.floor(ms / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n) => String(n).padStart(2, '0')
  if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

function MetaUpgradePromoBanner({
  promo,
  upgradeContact,
  provider,
  apiId,
  hideOnMeta = true,
  forceVariant,
}) {
  const theme = useTheme()
  const [dismissed, setDismissed] = useState(false)
  const [countdownLabel, setCountdownLabel] = useState(null)

  const isMeta = provider === 'hiperapi-v3-cloud-official'
  const isActive = !!promo?.isActive && isWithinWindow(promo?.startDate, promo?.endDate)
  const ctaUrl = promo?.ctaUrl || upgradeContact?.whatsappLink || null
  const ctaLabel = promo?.ctaLabel || (ctaUrl ? `Falar com ${upgradeContact?.brandName || 'especialista'}` : null)
  const accent = promo?.accentColor || theme.palette.primary.main
  const variant = forceVariant || promo?.variant || 'highlight'
  const dismissibleKey = useMemo(
    () => dismissKey(apiId, `${promo?.version || ''}:${variant}`),
    [apiId, promo?.version, variant]
  )

  useEffect(() => {
    if (!promo?.dismissible) {
      setDismissed(false)
      return
    }
    try {
      const raw = window.localStorage.getItem(dismissibleKey)
      if (!raw) return
      const ts = Number(raw)
      if (Number.isFinite(ts) && Date.now() - ts < DISMISS_TTL_MS) {
        setDismissed(true)
      } else {
        window.localStorage.removeItem(dismissibleKey)
      }
    } catch (e) {
      // ignore localStorage errors
    }
  }, [dismissibleKey, promo?.dismissible])

  useEffect(() => {
    if (!promo?.showCountdown || !promo?.endDate) {
      setCountdownLabel(null)
      return undefined
    }
    const update = () => {
      const remaining = new Date(promo.endDate).getTime() - Date.now()
      setCountdownLabel(formatCountdown(remaining))
    }
    update()
    const id = setInterval(update, COUNTDOWN_TICK_MS)
    return () => clearInterval(id)
  }, [promo?.showCountdown, promo?.endDate])

  if (hideOnMeta && isMeta) return null
  if (!isActive) return null
  if (dismissed) return null

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(dismissibleKey, String(Date.now()))
    } catch (e) {
      // ignore
    }
    setDismissed(true)
  }

  const handleCta = () => {
    if (!ctaUrl) return
    window.open(ctaUrl, '_blank', 'noopener,noreferrer')
  }

  if (variant === 'soft') {
    return (
      <div
        className="mb-8 border-l-4 pl-4 pr-3 py-2 flex items-center justify-between gap-4 flex-wrap"
        style={{ borderColor: accent }}
      >
        <div className="flex items-start gap-3 flex-1 min-w-[260px]">
          <AutoAwesomeIcon style={{ color: accent, fontSize: 22, marginTop: 2 }} />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {promo.badgeText && (
                <Chip
                  label={promo.badgeText}
                  size="small"
                  sx={{ backgroundColor: accent, color: '#fff', fontWeight: 700, height: 20 }}
                />
              )}
              <Text className="font-black">
                {promo.headline || 'WhatsApp API Oficial agora disponível'}
              </Text>
              <img src={theme.isDark ? metaWhite : metaPrimary} className="w-[64px]" alt="Meta" />
            </div>
            {promo.description && (
              <Text className="text-sm font-light opacity-90 mt-0.5">{promo.description}</Text>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {ctaUrl && ctaLabel && (
            <Button
              variant="contained"
              size="small"
              endIcon={<ArrowForwardIcon />}
              onClick={handleCta}
              sx={{ backgroundColor: accent, color: '#fff', fontWeight: 700, '&:hover': { backgroundColor: accent, opacity: 0.9 } }}
            >
              {ctaLabel}
            </Button>
          )}
          {promo.dismissible && (
            <IconButton size="small" onClick={handleDismiss} aria-label="Dispensar">
              <CloseIcon style={{ fontSize: 18 }} />
            </IconButton>
          )}
        </div>
      </div>
    )
  }

  const isUrgent = variant === 'urgent'
  const titleIcon = isUrgent ? (
    <WarningAmberIcon style={{ fontSize: 28, color: '#fff' }} />
  ) : (
    <RocketLaunchIcon style={{ fontSize: 28, color: '#fff' }} />
  )

  return (
    <Paper
      elevation={isUrgent ? 6 : 3}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderTop: `4px solid ${accent}`,
      }}
    >
      {promo.dismissible && (
        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label="Dispensar"
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
        >
          <CloseIcon style={{ fontSize: 18 }} />
        </IconButton>
      )}
      <div
        className="absolute top-0 right-0 w-[260px] h-[260px] rounded-full opacity-[0.08] pointer-events-none"
        style={{ backgroundColor: accent, transform: 'translate(40%, -40%)' }}
      />
      <div className="relative p-6 md:p-8 flex flex-col md:flex-row gap-6 md:items-center">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <div
              className="w-[44px] h-[44px] rounded-full flex items-center justify-center"
              style={{ backgroundColor: accent }}
            >
              {titleIcon}
            </div>
            {promo.badgeText && (
              <Chip
                label={promo.badgeText}
                size="small"
                sx={{
                  backgroundColor: accent,
                  color: '#fff',
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              />
            )}
            {countdownLabel && (
              <Chip
                label={`Termina em ${countdownLabel}`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: accent,
                  color: accent,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                }}
              />
            )}
            <img
              src={theme.isDark ? metaWhite : metaPrimary}
              className="w-[80px] ml-auto md:hidden"
              alt="Meta"
            />
          </div>

          <Text className="text-xl md:text-2xl font-black leading-tight">
            {promo.headline || 'Migre agora para a API Oficial do WhatsApp'}
          </Text>

          {promo.description && (
            <Text className="text-sm md:text-base font-light mt-2 opacity-90 leading-relaxed">
              {promo.description}
            </Text>
          )}

          {Array.isArray(promo.bullets) && promo.bullets.length > 0 && (
            <ul className="mt-4 space-y-1.5">
              {promo.bullets.filter(Boolean).map((b, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span
                    className="w-[6px] h-[6px] rounded-full mt-[9px] flex-shrink-0"
                    style={{ backgroundColor: accent }}
                  />
                  <Text className="text-sm md:text-base">{b}</Text>
                </li>
              ))}
            </ul>
          )}

          {ctaUrl && ctaLabel && (
            <div className="mt-5">
              <Button
                variant="contained"
                size="large"
                startIcon={<WhatsAppIcon />}
                onClick={handleCta}
                sx={{
                  backgroundColor: accent,
                  color: '#fff',
                  fontWeight: 800,
                  px: 3,
                  '&:hover': { backgroundColor: accent, opacity: 0.92 },
                }}
              >
                {ctaLabel}
              </Button>
            </div>
          )}
        </div>

        <div className="hidden md:flex flex-col items-center justify-center min-w-[180px]">
          <img
            src={theme.isDark ? metaWhite : metaPrimary}
            className="w-[160px]"
            alt="Meta"
          />
        </div>
      </div>
    </Paper>
  )
}

export default MetaUpgradePromoBanner
